import {
  Popover,
  PopoverContent,
  Command,
  CommandItem,
  CommandGroup,
  CommandEmpty,
  CommandInput,
  CommandList,
  PopoverTrigger,
  Button,
} from '@bpinternal/ui-kit-next'
import { Check, ChevronDown } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { cn } from '~/utils/cn'
import { FontMeta } from '../sections/Appearance'

type FontComboboxProps = {
  fonts: FontMeta[]
  fontFamily?: string
  setFont: (fontFamily: string) => void
}

const isSameFontFamily = (font1: string, font2: string): boolean => {
  return normalizeFontFamily(font1) === normalizeFontFamily(font2)
}

const normalizeFontFamily = (fontFamily: string): string => {
  return fontFamily.toLowerCase().trim()
}

export function FontCombobox({ fonts, fontFamily, setFont }: FontComboboxProps) {
  const initialDisplayCount = 10
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [displayCount, setDisplayCount] = useState(initialDisplayCount)

  // Reset display count when search changes
  const handleSearchChange = (value: string) => {
    setSearch(value)
    setDisplayCount(10)
  }

  const filteredFonts = useMemo(() => {
    if (!search) return fonts
    const needle = search.toLowerCase()
    return fonts.filter(
      (font) =>
        font.family.toLowerCase().includes(needle) ||
        (font.category ? font.category.toLowerCase().includes(needle) : false)
    )
  }, [fonts, search])

  const displayedFonts = filteredFonts.slice(0, displayCount)

  const hasMore = displayedFonts.length < filteredFonts.length

  const listRef = useRef<HTMLDivElement | null>(null)

  // Only use virtualization if we have more than 20 items to display
  const shouldVirtualize = displayedFonts.length > 20

  const rowVirtualizer = useVirtualizer({
    count: displayedFonts.length + (hasMore ? 1 : 0),
    getScrollElement: () => listRef.current,
    estimateSize: () => 44,
    overscan: 8,
    enabled: shouldVirtualize,
  })

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      setDisplayCount(initialDisplayCount)
      setSearch('')
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full min-w-36 max-w-full justify-between text-muted-foreground sm:max-w-52"
        >
          {fontFamily ? fonts.find((font) => isSameFontFamily(font.family, fontFamily))?.family : 'Select font...'}
          <ChevronDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search font..."
            className="h-9 border-none"
            value={search}
            onValueChange={handleSearchChange}
          />
          <CommandList ref={listRef} className="relative">
            {filteredFonts.length === 0 ? (
              <CommandEmpty>No fonts found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {shouldVirtualize ? (
                  <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      // Check if this is the "Load more" button
                      if (hasMore && virtualRow.index === displayedFonts.length) {
                        return (
                          <div
                            key="load-more"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => setDisplayCount((prev) => prev + 10)}
                              className="w-full rounded-sm px-2 py-1.5 text-center text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                            >
                              Load more fonts...
                            </button>
                          </div>
                        )
                      }

                      const font = displayedFonts[virtualRow.index]
                      if (!font) return null
                      return (
                        <CommandItem
                          key={font.family}
                          value={font.family}
                          onSelect={(currentValue) => {
                            if (!isSameFontFamily(currentValue, fontFamily || '')) {
                              setFont(currentValue)
                            }
                            setOpen(false)
                          }}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          <div className="flex w-full items-center justify-between">
                            <div className="flex flex-col">
                              <span className="font-medium">{font.family}</span>
                              <span className="text-xs text-muted-foreground">{font.category}</span>
                            </div>
                            <Check
                              className={cn(
                                'h-4 w-4',
                                isSameFontFamily(fontFamily || '', font.family) ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </div>
                        </CommandItem>
                      )
                    })}
                  </div>
                ) : (
                  <>
                    {displayedFonts.map((font) => (
                      <CommandItem
                        key={font.family}
                        value={font.family}
                        onSelect={(currentValue) => {
                          if (!isSameFontFamily(currentValue, fontFamily || '')) {
                            setFont(currentValue)
                          }
                          setOpen(false)
                        }}
                      >
                        <div className="flex w-full items-center justify-between">
                          <div className="flex flex-col">
                            <span className="font-medium">{font.family}</span>
                            <span className="text-xs text-muted-foreground">{font.category}</span>
                          </div>
                          <Check
                            className={cn(
                              'h-4 w-4',
                              isSameFontFamily(fontFamily || '', font.family) ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </div>
                      </CommandItem>
                    ))}
                    {hasMore && (
                      <button
                        type="button"
                        onClick={() => setDisplayCount((prev) => prev + 10)}
                        className="w-full rounded-sm px-2 py-1.5 text-center text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                      >
                        Load more fonts...
                      </button>
                    )}
                  </>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
